import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Project } from '../types';
import { computeLanes, nextDate, KIND_DOT, KIND_TEXT, KIND_LABEL } from '../utils/lanes';
import { getStatusLabel } from './ui/StatusBadge';
import { formatSvDateShort } from './ui/DateField';

const MAX_ROWS = 5;

/**
 * Defined at module level on purpose: a component created inside the parent's
 * render is a new type every render, so React remounts it, and on iOS a tap
 * whose touchstart and touchend land on different nodes never becomes a click.
 */
const Group: React.FC<{ title: string; items: Project[]; empty: string; showDate: boolean }> = ({ title, items, empty, showDate }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, MAX_ROWS);
  return (
  <section aria-label={title} className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]">
    <header className="flex items-center justify-between px-4 pt-3 pb-1.5">
      <h2 className="text-[13px] font-semibold text-[var(--text-primary)]">{title}</h2>
      <span className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-1.5 text-[12px] font-semibold tabular-nums ${items.length === 0 ? 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]' : 'bg-accent-100 text-accent-800 dark:bg-accent-900/40 dark:text-accent-200'}`}>{items.length}</span>
    </header>
    {items.length === 0 ? (
      <p className="px-4 pb-3 text-[13px] text-[var(--text-secondary)]">{empty}</p>
    ) : (
      <ul className="pb-1">
        {shown.map(p => {
          const n = nextDate(p);
          return (
            <li key={p.id}>
              <Link to={`/projects/${p.id}`} className="press-row flex items-center gap-3 px-4 min-h-[44px] py-1.5">
                <span aria-hidden="true" className={`h-2 w-2 rounded-full flex-shrink-0 ${KIND_DOT[n.kind]}`} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-medium text-[var(--text-primary)]">{p.address.street}</span>
                  <span className="block truncate text-[12px] text-[var(--text-secondary)]">
                    <span className={`font-medium ${KIND_TEXT[n.kind]}`}>{KIND_LABEL[n.kind]}</span>
                    {showDate ? ` ${formatSvDateShort(n.date)}` : ''}{n.time ? ` kl ${n.time}` : ''}
                  </span>
                </span>
                <span className="text-[12px] text-[var(--text-secondary)] flex-shrink-0">{getStatusLabel(p.status)}</span>
              </Link>
            </li>
          );
        })}
        {items.length > MAX_ROWS && (
          <li>
            <button type="button" aria-expanded={expanded} onClick={() => setExpanded(e => !e)} className="w-full text-left px-4 min-h-[44px] text-[15px] font-medium text-accent-700 dark:text-accent-300 press-row">
              {expanded ? 'Visa färre' : `+ ${items.length - MAX_ROWS} till`}
            </button>
          </li>
        )}
      </ul>
    )}
  </section>
  );
};

/**
 * Phone version of the desktop Idag / Denna vecka lanes: two stacked groups
 * at the top of the home list so the field team sees today's and this
 * week's visits and pickups first, and everything else sits below.
 * Read-only; rows open the project.
 */
const MobileLanes: React.FC<{ projects: Project[] }> = React.memo(({ projects }) => {
  const lanes = useMemo(() => computeLanes(projects), [projects]);

  return (
    <div className="space-y-3 mb-5">
      <Group title="Idag" items={lanes.today} empty="Inget bokat idag" showDate={false} />
      <Group title="Denna vecka" items={lanes.week} empty="Inget mer den här veckan" showDate />
    </div>
  );
});
MobileLanes.displayName = 'MobileLanes';

export default MobileLanes;
